import React, { useEffect } from "react";
import { ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import Nav from "./Nav";
import Footer from "./Footer";
import { EbookCard } from "./EbookCard";
import { EBOOKS } from "./ebooks";
import useSeo from "./useSeo";

export default function PlaybookPage() {
  useSeo(
    "Paid Media Ebooks | The Operator's Manual | The Digital Marketer",
    "The Operator's Manual — four end-to-end B2B paid media ebooks for LinkedIn Ads, Google Ads, Meta Ads and Bing Ads by Abhinav Dhiman."
  );
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const scrollToCollection = () =>
    document.getElementById("collection")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="playbook-page">
      <Nav />

      <section className="pt-[120px] md:pt-[160px] pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-signal mb-6">
              ◆ The Operator&apos;s Manual
            </p>
            <h1
              data-testid="playbook-heading"
              className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest"
            >
              Paid media. <span className="text-signal">End to end.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-ink2 leading-relaxed max-w-2xl">
              Four field manuals for running LinkedIn, Google, Meta and Bing as real B2B pipeline
              channels — structure, targeting, measurement, attribution and the checklists you run at
              launch. Written for the people who sit inside the ad accounts every week.
            </p>
            <button
              type="button"
              onClick={scrollToCollection}
              data-testid="playbook-explore-btn"
              className="mt-8 inline-flex items-center gap-3 bg-ink text-paper hover:bg-signal px-7 py-4 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal"
            >
              Explore the ebooks <ArrowDown size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      <section id="collection" className="py-14 md:py-20 bg-surface scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 01 — The collection</span>
            <span className="flex-1 h-px bg-line" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">Vol. 01 — 04</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="ebook-grid">
            {EBOOKS.map((b, i) => (
              <EbookCard key={b.slug} book={b} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 border-t border-ink pt-10">
          <h2 className="font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tightest max-w-3xl">
            Choose the platform you want to explore.
          </h2>
          <p className="mt-5 text-base md:text-lg text-ink2 leading-relaxed max-w-2xl">
            Each volume stands on its own — sixteen sections, five parts, ordered the way you would
            actually build the program. Start with the channel you run today, then add the next.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
